'use client';

import { useCallback, useId, useMemo } from 'react';

import type { SearchBreed } from '@/features/breeds';
import type { NormalizedCatalogSearchFilters } from '@/features/catalog';
import { getCitiesByProvince } from '@/features/locations';

import {
  buildBreedOptions,
  buildProvinceOptions,
  resolveCityLabel,
  resolveBreedLabel,
  resolveButtonLabel,
  resolveProvinceLabel
} from '../services/collapsibleSearchHeader';

export type CollapsibleSearchHeaderHookArgs = {
  breeds: SearchBreed[];
  selectedBreedLabel?: string;
  filters: NormalizedCatalogSearchFilters;
  onApplyFilters: (
    partial: Partial<Omit<NormalizedCatalogSearchFilters, 'page'>>
  ) => void;
  onExpandedChange: (expanded: boolean) => void;
};

/**
 * Build city options for the dropdown based on selected province.
 */
function buildCityOptions(
  provinceSlug: string | null | undefined
): Array<{ label: string; value: string | undefined }> {
  if (!provinceSlug) return [];

  const cities = getCitiesByProvince(provinceSlug);
  return [
    { label: 'All cities', value: undefined },
    ...cities.map((city) => ({
      label: city.name,
      value: city.slug
    }))
  ];
}

export function useCollapsibleSearchHeader({
  breeds,
  selectedBreedLabel,
  filters,
  onApplyFilters,
  onExpandedChange
}: CollapsibleSearchHeaderHookArgs) {
  const expandedPanelId = useId();
  const breedLabelId = useId();
  const provinceLabelId = useId();
  const cityLabelId = useId();

  const breedOptions = useMemo(() => buildBreedOptions(breeds), [breeds]);
  const provinceOptions = useMemo(() => buildProvinceOptions(), []);
  const cityOptions = useMemo(
    () => buildCityOptions(filters.province),
    [filters.province]
  );

  const breedDisplayLabel = useMemo(
    () => selectedBreedLabel ?? resolveBreedLabel(breeds, filters.breedSlug),
    [selectedBreedLabel, breeds, filters.breedSlug]
  );

  const provinceLabel = useMemo(
    () => resolveProvinceLabel(filters.province),
    [filters.province]
  );

  const cityLabel = useMemo(
    () => resolveCityLabel(filters.city),
    [filters.city]
  );

  const buttonLabel = useMemo(
    () => resolveButtonLabel(breedDisplayLabel, provinceLabel, cityLabel),
    [breedDisplayLabel, provinceLabel, cityLabel]
  );

  const handleBreedSelect = useCallback(
    (value: string | undefined) => {
      onApplyFilters({ breedSlug: value });
      // Don't collapse when selecting breed - allow user to continue selecting filters
      // The header will stay open via URL parameter
    },
    [onApplyFilters]
  );

  const handleProvinceSelect = useCallback(
    (value: string | undefined) => {
      // Clear city when province changes
      onApplyFilters({ province: value, city: undefined });
      // Don't collapse when selecting province - allow user to select city
    },
    [onApplyFilters]
  );

  const handleCitySelect = useCallback(
    (value: string | undefined) => {
      onApplyFilters({ city: value });
      // Don't collapse when selecting city - allow user to continue selecting filters
    },
    [onApplyFilters]
  );

  const handleExpand = useCallback(
    () => onExpandedChange(true),
    [onExpandedChange]
  );

  const handleCollapse = useCallback(
    () => onExpandedChange(false),
    [onExpandedChange]
  );

  // Show city dropdown only when a province is selected and there are cities
  const showCityDropdown = !!filters.province && cityOptions.length > 1;

  return {
    expandedPanelId,
    breedLabelId,
    provinceLabelId,
    cityLabelId,
    breedOptions,
    provinceOptions,
    cityOptions,
    buttonLabel,
    handleBreedSelect,
    handleProvinceSelect,
    handleCitySelect,
    handleExpand,
    handleCollapse,
    breedValue: filters.breedSlug ?? undefined,
    provinceValue: filters.province ?? undefined,
    cityValue: filters.city ?? undefined,
    showCityDropdown
  };
}
